import { useStore, type ViewKey } from "@/state/store";
import { Icon } from "@/components/atoms/Icons";
import { fmtUSD } from "@/components/atoms/format";
import { useEffect, useState } from "react";
import { api } from "@/api/client";

interface NavItem {
  key: ViewKey;
  label: string;
  icon: keyof typeof Icon;
}

const DISCOVER: NavItem[] = [
  { key: "markets", label: "Markets", icon: "Grid" },
  { key: "trending", label: "Trending", icon: "Trending" },
  { key: "sports", label: "Sports", icon: "Trophy" },
  { key: "politics", label: "Politics", icon: "Landmark" },
  { key: "crypto", label: "Crypto", icon: "Bitcoin" },
  { key: "culture", label: "Culture", icon: "Sparkles" },
  { key: "science", label: "Science", icon: "Info" },
];

const YOU: NavItem[] = [
  { key: "portfolio", label: "Portfolio", icon: "Briefcase" },
  { key: "activity", label: "Activity", icon: "Activity" },
  { key: "profile", label: "Profile", icon: "User" },
];

export function Sidebar() {
  const collapsed = useStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useStore((s) => s.toggleSidebar);
  const view = useStore((s) => s.view);
  const setView = useStore((s) => s.setView);
  const userId = useStore((s) => s.userId);
  const setDepositOpen = useStore((s) => s.setDepositOpen);
  const setWithdrawOpen = useStore((s) => s.setWithdrawOpen);
  const mobileNavOpen = useStore((s) => s.mobileNavOpen);
  const setMobileNavOpen = useStore((s) => s.setMobileNavOpen);
  const [cash, setCash] = useState<number>(0);

  // Esc chiude il drawer, come per i modali.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileNavOpen, setMobileNavOpen]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const fetchBalance = async () => {
      const { data } = await api.GET("/v1/wallet/balances");
      if (cancelled || !data) return;
      const usdc = data.items.find((b) => b.asset === "USDC");
      setCash(usdc ? Number(usdc.available) : 0);
    };
    fetchBalance();
    const t = setInterval(fetchBalance, 10_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [userId]);

  // Sotto `lg` il drawer e' sempre a larghezza piena (232px): "collassato" ha
  // senso solo quando la sidebar sottrae spazio al contenuto, e nel drawer non
  // lo fa.
  const width = collapsed ? 64 : 232;
  return (
    <>
      {/* Backdrop: solo mobile, e solo a drawer aperto. */}
      <div
        onClick={() => setMobileNavOpen(false)}
        aria-hidden
        className={`fixed inset-0 z-40 bg-black/60 transition-opacity duration-200 lg:hidden ${
          mobileNavOpen
            ? "opacity-100"
            : "pointer-events-none opacity-0"
        }`}
      />
      <aside
        style={{ width: 232 }}
        className={`fixed inset-y-0 left-0 z-50 h-screen border-r border-line bg-bg-1 transition-transform duration-[280ms] ease-[cubic-bezier(.2,.7,.2,1)] lg:hidden ${
          mobileNavOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <SidebarBody
          collapsed={false}
          view={view}
          setView={setView}
          userId={userId}
          cash={cash}
          setDepositOpen={setDepositOpen}
          setWithdrawOpen={setWithdrawOpen}
          onCollapse={() => setMobileNavOpen(false)}
          collapseLabel="Close"
        />
      </aside>

      {/* Da `lg` in su torna la sidebar classica, dentro al flusso. */}
      <aside
        style={{ width }}
        className="sticky top-0 hidden h-screen shrink-0 border-r border-line bg-bg-1 transition-[width] duration-[280ms] ease-[cubic-bezier(.2,.7,.2,1)] lg:block"
      >
        <SidebarBody
          collapsed={collapsed}
          view={view}
          setView={setView}
          userId={userId}
          cash={cash}
          setDepositOpen={setDepositOpen}
          setWithdrawOpen={setWithdrawOpen}
          onCollapse={toggleSidebar}
          collapseLabel="Collapse"
        />
      </aside>
    </>
  );
}

interface BodyProps {
  collapsed: boolean;
  view: ViewKey;
  setView: (v: ViewKey) => void;
  userId: string | null;
  cash: number;
  setDepositOpen: (v: boolean) => void;
  setWithdrawOpen: (v: boolean) => void;
  onCollapse: () => void;
  collapseLabel: string;
}

/** Contenuto condiviso fra drawer mobile e sidebar desktop: cambia solo il
 *  contenitore, non la nav. */
function SidebarBody({
  collapsed,
  view,
  setView,
  userId,
  cash,
  setDepositOpen,
  setWithdrawOpen,
  onCollapse,
  collapseLabel,
}: BodyProps) {
  return (
    <>
      <div className="flex h-14 items-center gap-2 px-4">
        <Icon.Logo size={24} />
        {!collapsed && (
          <span className="text-[15px] font-semibold tracking-tight">
            PredictionLeap
          </span>
        )}
      </div>

      <nav className="flex h-[calc(100vh-56px)] flex-col px-2 pb-3">
        <Section title="Discover" collapsed={collapsed}>
          {DISCOVER.map((n) => (
            <NavRow
              key={n.key}
              item={n}
              active={view === n.key}
              collapsed={collapsed}
              onClick={() => setView(n.key)}
            />
          ))}
        </Section>

        <Section title="You" collapsed={collapsed}>
          {YOU.map((n) => (
            <NavRow
              key={n.key}
              item={n}
              active={view === n.key}
              collapsed={collapsed}
              onClick={() => setView(n.key)}
            />
          ))}
        </Section>

        <div className="mt-auto">
          {!collapsed && userId && (
            <div className="mb-3 rounded-card border border-line bg-bg-2 p-3">
              <div className="text-[11px] uppercase tracking-wider text-muted">
                Cash
              </div>
              <div className="my-1 font-mono text-[18px] font-semibold">
                {fmtUSD(cash, { compact: false })}
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  className="btn flex-1 text-[12px]"
                  onClick={() => setDepositOpen(true)}
                >
                  Deposit
                </button>
                <button
                  className="btn flex-1 text-[12px]"
                  onClick={() => setWithdrawOpen(true)}
                >
                  Withdraw
                </button>
              </div>
            </div>
          )}
          <button
            className="btn-ghost flex w-full items-center gap-2 rounded-[10px] px-2 py-2 text-[12px]"
            onClick={onCollapse}
          >
            {collapsed ? <Icon.ChevronRight /> : <Icon.ChevronLeft />}
            {!collapsed && <span>{collapseLabel}</span>}
          </button>
        </div>
      </nav>
    </>
  );
}

function Section({
  title,
  collapsed,
  children,
}: {
  title: string;
  collapsed: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="my-2">
      {!collapsed && (
        <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-muted">
          {title}
        </div>
      )}
      <div className="flex flex-col gap-[2px]">{children}</div>
    </div>
  );
}

function NavRow({
  item,
  active,
  collapsed,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
}) {
  const IconCmp = Icon[item.icon];
  return (
    <button
      onClick={onClick}
      className={`relative flex w-full items-center gap-3 rounded-[10px] px-3 py-2 text-[13px] transition-colors ${
        active
          ? "bg-bg-3 text-text"
          : "text-text-2 hover:bg-white/[0.03] hover:text-text"
      }`}
    >
      {active && (
        <span className="absolute left-0 top-1/2 h-5 w-[2px] -translate-y-1/2 rounded-r bg-accent shadow-[0_0_12px_var(--accent)]" />
      )}
      <IconCmp size={16} />
      {!collapsed && <span>{item.label}</span>}
    </button>
  );
}
